import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper, Container, Stack, Alert, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../../config/api';

const ForgotPasswordPage = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);

        try {
            const { data } = await axios.post(getApiUrl('auth/forgot-password'), { email });
            setMessage(data.message);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#0B0F19',
                backgroundImage: 'radial-gradient(circle at 50% -20%, #1a237e 0%, #0B0F19 70%)'
            }}
        >
            <Box sx={{ position: 'absolute', top: 20, left: 20 }}>
                <Button 
                    startIcon={<ArrowBackIcon />} 
                    onClick={() => navigate('/login')}
                    sx={{ color: 'white', textTransform: 'none', fontWeight: 600 }}
                >
                    Back to Login
                </Button>
            </Box>
            <Container maxWidth="sm">
                <Paper
                    elevation={10}
                    sx={{
                        p: 5,
                        borderRadius: 4,
                        bgcolor: 'rgba(255,255,255,0.05)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}
                >
                    <Stack spacing={3} alignItems="center">
                        <Box sx={{ width: 50, height: 50, bgcolor: '#ffab00', borderRadius: 2 }} />
                        <Typography variant="h4" fontWeight="bold" color="white">Trouble logging in?</Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.6)" textAlign="center">
                            Enter your email address and we'll send you a secure link to reset your password.
                        </Typography>

                        {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
                        {message && <Alert severity="success" sx={{ width: '100%' }}>{message}</Alert>}

                        {!message ? (
                            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                                <Stack spacing={3}>
                                    <TextField
                                        label="Email Address"
                                        variant="outlined"
                                        fullWidth
                                        required
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                        InputProps={{
                                            style: { color: 'white' },
                                            sx: { bgcolor: 'rgba(255,255,255,0.05)' }
                                        }}
                                    />

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        disabled={loading}
                                        sx={{
                                            bgcolor: '#ffab00',
                                            color: 'black',
                                            fontWeight: 'bold',
                                            py: 1.5,
                                            '&:hover': { bgcolor: '#ffca28' }
                                        }}
                                    >
                                        {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
                                    </Button>
                                </Stack>
                            </form>
                        ) : (
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => navigate('/login')}
                                sx={{ 
                                    color: '#ffab00', 
                                    borderColor: '#ffab00',
                                    fontWeight: 'bold',
                                    '&:hover': { borderColor: '#ffca28', color: '#ffca28' }
                                }}
                            >
                                Return to Login
                            </Button>
                        )}
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
};

export default ForgotPasswordPage;
